/**
 * Recipes: what a dish is made of, and what it costs to put on a plate.
 *
 * Three things the types are deliberate about.
 *
 * Every line carries `quantity` *and* `raw_quantity`, and neither ever stands
 * in for the other. "320 g of fillet" and "516 g of fish off the shelf" are
 * both true, and the gap between them is the most common costing error there
 * is — it always errs in the direction that flatters the margin. `yield_factor`
 * is the explanation that sits between them.
 *
 * `margin`, `margin_percent` and `food_cost_percent` are `string | null`, and
 * null means no menu price was given. Not zero. "We cannot tell you the
 * margin" and "the margin is nothing" are different answers, and only one of
 * them is safe to price a menu from.
 *
 * `usage` comes back as rows carrying name and unit, not a map of ids to
 * numbers, so a prep sheet does not have to fetch the whole stock list to
 * print "1 kg of whole sea bass".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import type { Allergen } from './inventory';
import { useApi } from './provider';

export type RecipeKind = 'dish' | 'prep';

/**
 * Two different things, not one thing with a badge.
 *
 * A dish is what a guest orders; a prep is what a chef makes in advance to use
 * in other recipes, and it has a stock item of its own so a batch can be
 * counted on a shelf like anything else.
 */
export const RECIPE_KIND_LABELS: Record<RecipeKind, string> = {
  dish: 'Dish',
  prep: 'Prep item',
};

export interface RecipeLine {
  id: string;
  /** Exactly one of these two is set: a line is an ingredient or a sub-recipe. */
  stock_item_id: string | null;
  component_recipe_id: string | null;
  name: string;
  quantity: string;
  unit: string;
  /**
   * Usable output over raw input. A whole fish filleting at 60% is 0.6; a kilo
   * of dry rice becoming 2.5 kg cooked is 2.5. Consumption divides by it.
   */
  yield_factor: string;
  /** What actually leaves the store, in the unit the line was written in. */
  raw_quantity: string;
  note: string | null;
  sort_order: number;
}

export interface Recipe {
  id: string;
  code: string;
  name: string;
  kind: RecipeKind;
  /** Set on a dish: the menu item this is the recipe for. */
  menu_item_id: string | null;
  /** Set on a prep: the stock item a batch of this becomes. */
  produces_item_id: string | null;
  /** What one run makes, measured after cooking — not the sum of the lines. */
  batch_yield: string;
  batch_unit: string;
  method: string | null;
  is_active: boolean;
  lines: RecipeLine[];
}

export interface CostLine {
  ref: string;
  kind: 'stock' | 'recipe';
  name: string;
  quantity: string;
  unit: string;
  raw_quantity: string;
  unit_cost: string;
  cost: string;
}

export interface RecipeCost {
  recipe_id: string;
  name: string;
  batch_cost: string;
  batch_yield: string;
  batch_unit: string;
  /** Cost per unit of yield — what a portion of this is worth. */
  unit_cost: string;
  menu_price: string | null;
  /** Null when no price was given. Never zero — see the note at the top. */
  margin: string | null;
  margin_percent: string | null;
  food_cost_percent: string | null;
  calculated_at: string;
  lines: CostLine[];
}

export interface RecipeUsageLine {
  stock_item_id: string;
  name: string;
  unit: string;
  quantity: string;
}

export interface CreateRecipeInput {
  code: string;
  name: string;
  kind?: RecipeKind;
  menu_item_id?: string;
  produces_item_id?: string;
  batch_yield?: string;
  batch_unit?: string;
  method?: string;
}

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['recipes'] });
}

/**
 * The recipe list, optionally one kind of it.
 *
 * The filter is here because dishes and preps are two lists on a screen rather
 * than one list with a badge on some rows.
 */
export function useRecipes(kind?: RecipeKind, includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'list', kind ?? null, includeInactive],
    queryFn: ({ signal }) =>
      api.get<Recipe[]>(
        `/v1/recipes?include_inactive=${includeInactive}${kind ? `&kind=${kind}` : ''}`,
        signal,
      ),
    staleTime: 60_000,
  });
}

export function useRecipe(recipeId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'one', recipeId ?? null],
    queryFn: ({ signal }) => api.get<Recipe>(`/v1/recipes/${recipeId}`, signal),
    enabled: Boolean(recipeId),
    staleTime: 30_000,
  });
}

export function useCreateRecipe() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Recipe, ApiError, CreateRecipeInput>({
    mutationFn: (input) => api.post<Recipe>('/v1/recipes', input),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Put an ingredient on a recipe.
 *
 * `yieldFactor` defaults to 1, meaning nothing is lost between buying and
 * using. Anything else is the trim, and it is what makes the plate cost
 * honest.
 */
export function useAddRecipeLine() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Recipe,
    ApiError,
    {
      recipeId: string;
      stockItemId: string;
      quantity: string;
      unit: string;
      yieldFactor?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<Recipe>(`/v1/recipes/${input.recipeId}/lines`, {
        stock_item_id: input.stockItemId,
        quantity: input.quantity,
        unit: input.unit,
        yield_factor: input.yieldFactor ?? '1',
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * Put a sub-recipe on a recipe. Enterprise.
 *
 * A separate route from `useAddRecipeLine` because the gate is on this one act
 * — a Professional site builds and costs flat recipes without meeting a
 * paywall anywhere else. A line that would make a loop is refused here with
 * the chain named, rather than at costing time.
 */
export function useAddSubRecipe() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Recipe,
    ApiError,
    {
      recipeId: string;
      componentRecipeId: string;
      quantity: string;
      unit: string;
      yieldFactor?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<Recipe>(`/v1/recipes/${input.recipeId}/sub-recipes`, {
        component_recipe_id: input.componentRecipeId,
        quantity: input.quantity,
        unit: input.unit,
        yield_factor: input.yieldFactor ?? '1',
        note: input.note ?? null,
      }),
    onSettled: () => invalidate(queryClient),
  });
}

export function useRemoveRecipeLine() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<Recipe, ApiError, { recipeId: string; lineId: string }>({
    mutationFn: ({ recipeId, lineId }) =>
      api.request<Recipe>(`/v1/recipes/${recipeId}/lines/${lineId}`, { method: 'DELETE' }),
    onSettled: () => invalidate(queryClient),
  });
}

/**
 * What a recipe costs, and what it makes at a given price.
 *
 * A query, because costing is a read — it fetches every stock price now and
 * writes nothing. So a cost panel re-renders as a chef types a different
 * price, and trying one leaves nothing behind.
 *
 * `menuPrice` is genuinely optional and is left off the request when absent,
 * so the margin fields come back null rather than as a margin measured against
 * zero. Pass the linked menu item's price on a dish and the common case has a
 * real answer without anybody typing.
 *
 * Never cached: ingredient prices move with every delivery, and a plate cost
 * that is quietly out of date is exactly what deriving it on demand avoids.
 */
export interface UndeclaredIngredient {
  stock_item_id: string;
  name: string;
}

export interface DishAllergens {
  recipe_id: string;
  name: string;
  menu_item_id: string | null;
  /** Rolled up from the ingredients, sub-recipes included. */
  allergens: Allergen[];
  /**
   * Ingredients nobody has declared anything for. While this is not empty,
   * `allergens` is a lower bound rather than an answer — and nothing here is
   * safe to print.
   */
  undeclared: UndeclaredIngredient[];
  complete: boolean;
}

/** Every dish and what it carries, unfinished ones first. */
export function useAllergenMatrix() {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'allergens'],
    queryFn: ({ signal }) => api.get<DishAllergens[]>('/v1/recipes/allergens', signal),
    staleTime: 60_000,
  });
}

/** What one dish carries, and what nobody has said about. */
export function useDishAllergens(recipeId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'allergens', recipeId],
    queryFn: ({ signal }) =>
      api.get<DishAllergens>(`/v1/recipes/${recipeId}/allergens`, signal),
    enabled: Boolean(recipeId),
    staleTime: 60_000,
  });
}

export interface VarianceLine {
  stock_item_id: string;
  name: string;
  unit: string;
  /** What the recipes say the period's sales should have taken. */
  theoretical: string;
  /** What the store actually gave up: waste, and what a count found missing. */
  actual: string;
  /** Actual minus theoretical. Positive is the direction worth an afternoon. */
  variance: string;
  /** Null when nothing was theoretically used: neither 0% nor infinite. */
  variance_percent: string | null;
}

export interface Variance {
  location_id: string;
  from_date: string;
  to_date: string;
  /** Dishes sold with no recipe. Each one inflates the variance below. */
  unrecipied: string[];
  lines: VarianceLine[];
}

/**
 * What the recipes say should have gone, against what did.
 *
 * Actual deliberately excludes the automatic depletion the recipes themselves
 * produced — comparing those would compare a number with itself and report a
 * perfect kitchen every week. A site that never counts sees almost nothing
 * here, which is the truth: nobody has looked.
 */
export function useVariance(locationId: string | undefined, fromDate: string, toDate: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'variance', locationId, fromDate, toDate],
    queryFn: ({ signal }) =>
      api.get<Variance>(
        `/v1/recipes/variance?location_id=${locationId}&from_date=${fromDate}&to_date=${toDate}`,
        signal,
      ),
    enabled: Boolean(locationId),
    staleTime: 60_000,
  });
}

export function useRecipeCost(recipeId: string | undefined, menuPrice?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'cost', recipeId ?? null, menuPrice ?? null],
    queryFn: ({ signal }) =>
      api.get<RecipeCost>(
        `/v1/recipes/${recipeId}/cost${menuPrice ? `?menu_price=${menuPrice}` : ''}`,
        signal,
      ),
    enabled: Boolean(recipeId),
    staleTime: 0,
  });
}

/**
 * What a number of servings takes out of the store.
 *
 * Flattened to raw goods with every yield factor applied — sub-recipes
 * disappear, because the store holds bones rather than sauce. Biggest first,
 * which is the order a prep sheet is read in.
 */
export function useRecipeUsage(recipeId: string | undefined, servings = '1') {
  const api = useApi();
  return useQuery({
    queryKey: ['recipes', 'usage', recipeId ?? null, servings],
    queryFn: ({ signal }) =>
      api.get<RecipeUsageLine[]>(`/v1/recipes/${recipeId}/usage?servings=${servings}`, signal),
    enabled: Boolean(recipeId),
    staleTime: 30_000,
  });
}

/**
 * The chain from a circular-recipe refusal, ready to render as a chain.
 *
 * The server says "These recipes contain each other: sauce → stock → sauce",
 * which tells somebody what to go and fix. This splits it so a screen can show
 * the loop as steps without rewording the sentence.
 */
/**
 * A quantity as a prep sheet would write it: "516 g", "0.5 kg", "2 ea".
 *
 * The API is consistent at six decimal places, which is the right precision to
 * carry and the wrong one to print — 516.129032 g is a number nobody weighs.
 * Three places is the most a kitchen scale resolves, and trailing zeros go, so
 * the same helper reads well for both half a gram of saffron and 25 kg of
 * flour. Stated once here so a screen and an export cannot disagree.
 */
export function formatRecipeQuantity(quantity: string, unit: string): string {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return `${quantity} ${unit}`;
  return `${Number(value.toFixed(3))} ${unit}`;
}

export function cycleChain(message: string): string[] {
  const [, chain] = message.split(': ');
  if (!chain) return [];
  return chain
    .split('→')
    .map((part) => part.trim())
    .filter(Boolean);
}
